import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import pushRulesService from "../services/pushRulesService";
import streamService from "../services/streamService";
import "./RulesDrawer.css";

const CONDITIONS = [
  { value: "below", label: "Below" },
  { value: "above", label: "Above" },
];

const emptyForm = {
  device: "inverter",
  field: "",
  condition: "below",
  value: "",
  threshold: "",
  enabled: true,
};

const RulesDrawer = ({ open, onClose }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState([]);
  const [devices, setDevices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pushRulesService.getRules();
      setRules(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFields = useCallback(async () => {
    try {
      const data = await pushRulesService.getFields();
      setFields(data || []);
    } catch (err) {
      console.error("Failed to load fields:", err);
    }
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      const streams = await streamService.getAvailableStreams();
      const devs = [{ id: "inverter", name: "Inverter" }];
      Object.values(streams).forEach((s) => {
        devs.push({ id: s.id, name: s.name || s.id });
      });
      setDevices(devs);
    } catch (err) {
      setDevices([{ id: "inverter", name: "Inverter" }]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadRules();
      loadFields();
      loadDevices();
    }
  }, [open, loadRules, loadFields, loadDevices]);

  const openAddForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEditForm = (rule) => {
    setEditingId(rule.ID);
    setForm({
      device: rule.device,
      field: rule.field,
      condition: rule.condition,
      value: rule.value,
      threshold: rule.threshold,
      enabled: rule.enabled,
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        device: form.device,
        field: form.field,
        condition: form.condition,
        value: parseFloat(form.value) || 0,
        threshold: parseFloat(form.threshold) || 0,
        enabled: form.enabled,
      };
      if (editingId) {
        await pushRulesService.updateRule(editingId, payload);
      } else {
        await pushRulesService.createRule(payload);
      }
      setShowForm(false);
      setEditingId(null);
      await loadRules();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await pushRulesService.deleteRule(id);
      await loadRules();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggle = async (rule) => {
    try {
      await pushRulesService.updateRule(rule.ID, {
        ...rule,
        enabled: !rule.enabled,
      });
      await loadRules();
    } catch (err) {
      setError(err.message);
    }
  };

  const getFieldLabel = (key) => {
    const f = fields.find((f) => f.key === key);
    return f ? `${f.label} (${f.unit})` : key;
  };

  const getDeviceName = (id) => {
    const d = devices.find((d) => d.id === id);
    return d ? d.name : id;
  };

  const isCamera = form.device !== "inverter";

  return createPortal(
    <>
      <div
        className={`rules-overlay ${open ? "open" : ""}`}
        onClick={onClose}
      />
      <div className={`rules-drawer ${open ? "open" : ""}`}>
        <div className="rules-header">
          <h2>Push Rules</h2>
          <button type="button" className="rules-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="rules-actions">
          <button
            type="button"
            className="rules-add-btn"
            onClick={openAddForm}
          >
            + Add Rule
          </button>
          <button
            type="button"
            className="rules-refresh-btn"
            onClick={loadRules}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
        {error && <div className="rules-error">{error}</div>}

        {showForm && (
          <div className="rules-form">
            <div className="rules-form-title">
              {editingId ? "Edit Rule" : "New Rule"}
            </div>
            <label className="rules-label">
              Device
              <select
                value={form.device}
                onChange={(e) =>
                  setForm({ ...form, device: e.target.value, field: "" })
                }
              >
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>

            {isCamera ? (
              <div className="rules-coming-soon">
                Camera notifications are in development
              </div>
            ) : (
              <>
                <label className="rules-label">
                  Field
                  <select
                    value={form.field}
                    onChange={(e) =>
                      setForm({ ...form, field: e.target.value })
                    }
                  >
                    <option value="">Select field...</option>
                    {fields
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label} ({f.unit})
                        </option>
                      ))}
                  </select>
                </label>
                <label className="rules-label">
                  Condition
                  <select
                    value={form.condition}
                    onChange={(e) =>
                      setForm({ ...form, condition: e.target.value })
                    }
                  >
                    {CONDITIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="rules-label">
                  Value
                  <input
                    type="number"
                    value={form.value}
                    onChange={(e) =>
                      setForm({ ...form, value: e.target.value })
                    }
                    placeholder="e.g. 50"
                  />
                </label>
                <label className="rules-label">
                  Reset Threshold
                  <input
                    type="number"
                    value={form.threshold}
                    onChange={(e) =>
                      setForm({ ...form, threshold: e.target.value })
                    }
                    placeholder="e.g. 10"
                  />
                </label>
                <div className="rules-threshold-hint">
                  {form.condition === "below" && form.value && form.threshold
                    ? `Fires when value drops below ${form.value}. Resets when value rises above ${
                        parseFloat(form.value) + parseFloat(form.threshold)
                      }.`
                    : form.condition === "above" && form.value && form.threshold
                    ? `Fires when value rises above ${form.value}. Resets when value drops below ${
                        parseFloat(form.value) - parseFloat(form.threshold)
                      }.`
                    : ""}
                </div>
              </>
            )}

            <div className="rules-form-buttons">
              <button
                type="button"
                className="rules-save-btn"
                onClick={handleSave}
                disabled={saving || isCamera || !form.field}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className="rules-cancel-btn"
                onClick={cancelForm}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="rules-content">
          {rules.length === 0 && !loading && !error && (
            <div className="rules-empty">No push rules configured yet.</div>
          )}
          <ul className="rules-list">
            {rules.map((rule) => (
              <li
                key={rule.ID}
                className={`rules-item ${rule.enabled ? "" : "disabled"}`}
              >
                <div className="rules-item-main">
                  <span className="rules-item-device">
                    {getDeviceName(rule.device)}
                  </span>
                  <span className="rules-item-condition">
                    {getFieldLabel(rule.field)} {rule.condition}{" "}
                    {rule.value}
                  </span>
                </div>
                <div className="rules-item-meta">
                  <span className="rules-item-threshold">
                    Threshold: {rule.threshold}
                  </span>
                  {rule.triggered && (
                    <span className="rules-item-triggered">triggered</span>
                  )}
                </div>
                <div className="rules-item-actions">
                  <button
                    type="button"
                    className={`rules-toggle-btn ${
                      rule.enabled ? "on" : "off"
                    }`}
                    onClick={() => handleToggle(rule)}
                  >
                    {rule.enabled ? "On" : "Off"}
                  </button>
                  <button
                    type="button"
                    className="rules-edit-btn"
                    onClick={() => openEditForm(rule)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="rules-delete-btn"
                    onClick={() => handleDelete(rule.ID)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>,
    document.body
  );
};

export default RulesDrawer;
